import { gql, type TypedDocumentNode } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { type CSSProperties } from 'styled-components';

import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { Star } from '@sorare/core/src/atoms/icons/Star';
import { iconX } from '@sorare/core/src/atoms/icons/system/iconX.ts';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BrandM } from '@sorare/core/src/atoms/typography';
import { TextureBackground } from '@sorare/core/src/atoms/ui/TextureBackground';
import { Trapezoid } from '@sorare/core/src/atoms/ui/Trapezoid';
import { Dialog } from '@sorare/core/src/components/dialog';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { glossary } from '@sorare/core/src/lib/glossary';
import { useCommonGameSet } from '@sorare/core/src/set';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';
import { SeriesBalance } from '@sorare/marketplace/src/components/commonCard/BuyPacks/SeriesBalance';

import { CollectionMissions } from '#src/components/missions/CollectionMissions/index.tsx';
import { SetCollectionTaskDetails } from '#src/components/missions/SetCollectionTask/SetCollectionTaskDialog/SetCollectionTaskDetails/index.tsx';
import { getTaskCompetitionColor } from '#src/lib/task/index.tsx';

import type {
  CollectMissionsDialogQuery,
  CollectMissionsDialogQueryVariables,
  CollectMissionsTaskDetailsQuery,
  CollectMissionsTaskDetailsQueryVariables,
} from './__generated__/index.graphql.ts';

const COLLECT_MISSIONS_DIALOG_QUERY = gql`
  query CollectMissionsDialogQuery($sport: Sport!) {
    currentUser {
      slug
      ...CollectionMissions_currentUser
    }
  }
  ${CollectionMissions.fragments.currentUser}
` as TypedDocumentNode<
  CollectMissionsDialogQuery,
  CollectMissionsDialogQueryVariables
>;

const COLLECT_MISSIONS_TASK_DETAILS_QUERY = gql`
  query CollectMissionsTaskDetailsQuery(
    $taskId: ID!
    $user: String!
    $cardSetSlug: String!
    $sport: Sport!
  ) {
    currentUser {
      slug
      task(id: $taskId) {
        id
        ...SetCollectionTaskDetails_taskInterface
        ...getTaskCompetitionColor_taskInterface
      }
      ...SetCollectionTaskDetails_currentUser
    }
  }
  ${SetCollectionTaskDetails.fragments.taskInterface}
  ${getTaskCompetitionColor.fragments.taskInterface}
  ${SetCollectionTaskDetails.fragments.currentUser}
` as TypedDocumentNode<
  CollectMissionsTaskDetailsQuery,
  CollectMissionsTaskDetailsQueryVariables
>;

const ContentWrapper = styled(Vertical).attrs({ gap: 0 })`
  position: relative;
  z-index: 1;
  overflow: hidden;
  max-height: 90vh;
  width: min(calc(80 * var(--vw)), 1000px);
`;

const HeaderRow = styled(SBHorizontal)`
  padding: var(--double-unit);
  align-items: center;
  flex-shrink: 0;
  border-bottom: 1px solid var(--c-nd-150);
`;

const HeaderTitle = styled(Horizontal).attrs({ gap: 0.5, center: true })`
  flex: 1;
  justify-content: center;
  color: var(--c-black);
`;

const TwoPanelLayout = styled.div`
  display: grid;
  grid-template-columns: 424px 1fr;
  overflow: hidden;
`;

const LeftPanel = styled(Vertical).attrs({ gap: 0 })`
  overflow-y: auto;
  padding: var(--double-unit);
  border-right: 1px solid var(--c-nd-150);
`;

const RightPanel = styled(Vertical).attrs({ gap: 0 })`
  overflow: hidden;
  flex: 1;
  min-width: 0;
`;

const TaskDetailsWrapper = styled(Vertical).attrs({ gap: 3 })`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  padding: var(--double-unit);
  position: relative;
  @media ${laptopAndAbove} {
    padding: var(--triple-unit);
  }
`;

const StyledTextureBackground = styled(TextureBackground)`
  position: absolute;
  inset: 0;
  height: 40%;
  z-index: -1;
  &:after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      180deg,
      rgba(var(--c-rgb-black), 0) 0%,
      rgba(var(--c-rgb-black), 1) 50%
    );
  }
`;

type Props = {
  open: boolean;
  onClose: () => void;
};

export const CollectMissionsDialog = ({ open, onClose }: Props) => {
  const commonGameSet = useCommonGameSet();
  const { sport } = useSportContext();
  const { currentUser: contextCurrentUser } = useCurrentUserContext();
  const { data } = useQuery(COLLECT_MISSIONS_DIALOG_QUERY, {
    variables: {
      sport,
    },
    skip: !open,
  });

  const { setCollectionTasks } = data?.currentUser || {};
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);

  if (!selectedTaskId && setCollectionTasks?.length && open) {
    setSelectedTaskId(setCollectionTasks[0].id);
  }

  const { data: taskData } = useQuery(COLLECT_MISSIONS_TASK_DETAILS_QUERY, {
    variables: {
      taskId: idFromObject(selectedTaskId || ''),
      user: contextCurrentUser?.slug ?? '',
      cardSetSlug: commonGameSet.slug,
      sport,
    },
    skip: !selectedTaskId,
  });

  const task = taskData?.currentUser?.task;

  return (
    <Dialog open={open} onClose={onClose} hideHeader>
      <ContentWrapper>
        <HeaderRow>
          <SeriesBalance cardSetSlugs={[commonGameSet.slug]} />
          <HeaderTitle>
            <Trapezoid
              style={
                {
                  '--background': 'var(--c-set-surface-primary)',
                } as CSSProperties
              }
            >
              <Horizontal center gap={0.5}>
                <Star />
                <BrandM>
                  <FormattedMessage {...glossary.collect} />
                </BrandM>
              </Horizontal>
            </Trapezoid>
          </HeaderTitle>
          <IconButton
            size="small"
            onClick={onClose}
            icon={iconX}
            color="quaternary"
          />
        </HeaderRow>

        <TwoPanelLayout>
          <LeftPanel>
            <CollectionMissions
              currentUser={data?.currentUser}
              onTaskClick={setSelectedTaskId}
              selectedTaskId={selectedTaskId}
            />
          </LeftPanel>
          <RightPanel>
            {!!task && (
              <TaskDetailsWrapper>
                <StyledTextureBackground
                  background="wave2"
                  mode="light"
                  color={getTaskCompetitionColor(
                    task,
                    'var(--c-set-surface-primary)'
                  )}
                />
                <SetCollectionTaskDetails
                  task={task}
                  currentUser={taskData?.currentUser}
                />
              </TaskDetailsWrapper>
            )}
          </RightPanel>
        </TwoPanelLayout>
      </ContentWrapper>
    </Dialog>
  );
};
